import React, { useEffect, useState } from "react";

/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { GameClient } from "../gameClient";
import { UIButton, UIButtonSmall } from "../styles/ButtonStyle";
import { Sound } from "../../../shared/sound";
import { getTrainingLevel } from "../userSettings";
import { getQueryParams, openSteam } from "../../../shared/utils";
import { TrainingLevel } from "../../../shared/playerAccount";
import { P2PPrivacyWarning, PrivacyPolicyLink } from "./PrivacyWarning";
import { isSteamNetworkingAvailable } from "../networking/steamNetworking";
import { openSteamInviteDialog } from "../networking/steamLobby";
import { Multiplayer } from "../../../shared/constants";
import { clearPendingSteamLobbyId, getPendingSteamLobbyId, onSteamInvite } from "../networking/pendingSteamInvite";
import { clearConnectionStatus, MultiplayerConnectionStatus, setConnectionStep, useHasConnectionError } from "./MultiplayerConnectionStatus";
import { ExperimentalStamp } from "./ExperimentalStamp";
import { readContinuableMultiplayerSave } from "../../../shared/savedGameUtils";

declare var gameClient: GameClient;

// Selections a user can make while clicking through the start game tree.
// The tree itself doesn't know how to act on these - it just emits the
// selection back up via onGameTreeSelection for the caller to handle.
export enum GameTreeSelection {
    Continue = 'Continue',
    Solo = 'Solo',
    Ranked = 'Ranked',
    Settings = 'Settings',
    ContinueMultiplayer = 'ContinueMultiplayer',
    NewMultiplayerGame = 'NewMultiplayerGame',
    HostViaSteam = 'HostViaSteam',
    HostViaBrowser = 'HostViaBrowser',
    JoinViaSteam = 'JoinViaSteam',
    JoinViaBrowser = 'JoinViaBrowser',
    StartPeerToPeer = 'StartPeerToPeer',
    SkipTutorial = 'SkipTutorial',
    Quit = 'Quit',
    LeaveLobby = 'LeaveLobby',
}

const containerStyle = css`
    user-select:none;
    overflow: hidden;
    max-height: 100vh;
    margin: auto;
    position: absolute;
    top:0;
    bottom:0;
    left:0;
    right:0;
    color: white;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
`;

const menuStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width 20vw;
    height: 100%;
`;

const buttonStyle = css`
    margin-bottom: 2.5vh;
`;

const logoStyle = css`
    width: 80vh;
    height: 50vh;
`;

interface LobbyProps {
    isSpawnGame: boolean;
    hostId?: number;
    mpAllowed: boolean;
    connected: boolean;
    buttonText: string;
    peerConnected: boolean;
    canContinue: boolean;
    startPending: boolean;
    onGameTreeSelection: (selection: GameTreeSelection) => void | Promise<void>;
}

type LayoutNames = 'Training' |
    'Lobby' |
    'Play' |
    'Multiplayer' |
    'HostSteam' |
    'HostBrowser' |
    'JoinBrowser' |
    'Spawn';

interface TreeProps extends LobbyProps {
    layout: LayoutNames;
    setLayout: (layout: LayoutNames) => void;
    hover: () => void;
    retry: () => void;
    backOut: () => void;
}

// Steam networking is only available in the desktop (Electron) build; the browser
// build talks to peers over WebRTC.
type Transport = 'steam' | 'browser';

interface TransportProps extends TreeProps {
    transport: Transport;
}

const multiplayerLayouts: LayoutNames[] = [
    'Multiplayer',
    'HostSteam',
    'HostBrowser',
    'JoinBrowser',
    'Spawn'
];

const onButtonExit = () => {
    document.getElementById('lobby_tooltip')!.innerHTML = '';
    document.getElementById('lobby_tooltip')!.style.display = 'none';
}

const setTooltipText = (text: string) => {
    document.getElementById('lobby_tooltip')!.style.display = 'block';
    document.getElementById('lobby_tooltip')!.innerHTML = text;
}


export const StartGameTree = (props: LobbyProps) => {

    const [layout, setLayout] = React.useState<LayoutNames>('Lobby');

    const hover = () => {
        if (gameClient && props.connected) {
            gameClient.playUISound(Sound.Menu);
        }
    }

    useEffect(() => {
        const queryParams = getQueryParams(window.location.href);
        const lobbyToJoin = queryParams['lobby'];
        console.log('reading url params');
        if (getPendingSteamLobbyId()) {
            joinSteamGame();
        } else if (lobbyToJoin) {
            setLayout('JoinBrowser');
        }
    }, []);

    // The invite listener is registered once, so keep it reading current values
    const layoutRef = React.useRef(layout);
    const onSelectionRef = React.useRef(props.onGameTreeSelection);
    useEffect(() => {
        layoutRef.current = layout;
        onSelectionRef.current = props.onGameTreeSelection;
    });

    // Accepting an invite on Steam is the player's decision to join, so act on it
    useEffect(() => onSteamInvite(async () => {
        const layoutWithLobby = layoutRef.current === 'HostSteam' ||
            layoutRef.current === 'HostBrowser' ||
            layoutRef.current === 'Spawn';
        if (layoutWithLobby) {
            // Drop the lobby we were hosting or waiting in before joining the invite,
            // otherwise its connection is left running behind us
            await onSelectionRef.current(GameTreeSelection.LeaveLobby);
        }
        joinSteamGame();
    }), []);

    //Remembered so Retry can repeat the attempt the player actually made
    const lastConnectSelection = React.useRef<GameTreeSelection | null>(null);
    const connectSelections = [
        GameTreeSelection.HostViaSteam,
        GameTreeSelection.HostViaBrowser,
        GameTreeSelection.JoinViaSteam,
        GameTreeSelection.JoinViaBrowser
    ];

    const onGameTreeSelection = (selection: GameTreeSelection) => {
        if (connectSelections.includes(selection)) {
            lastConnectSelection.current = selection;
        }
        return props.onGameTreeSelection(selection);
    };

    //A steam invite carries the lobby with it, so there is nothing to confirm - go straight in
    const joinSteamGame = () => {
        if (gameClient.gameId !== null) return;
        onGameTreeSelection(GameTreeSelection.JoinViaSteam);
        setLayout('Spawn');
    };

    const retry = async () => {
        const selection = lastConnectSelection.current;
        if (!selection) return;
        //LeaveLobby disposes the in progress connection, closes anything that came up, and
        //clears the status list, so the new attempt starts from nothing
        await props.onGameTreeSelection(GameTreeSelection.LeaveLobby);
        await onGameTreeSelection(selection);
    };

    //Where a player belongs when they abandon an attempt: a join link tab has nowhere further
    //back to go than its own connect screen
    const backOut = async () => {
        await props.onGameTreeSelection(GameTreeSelection.LeaveLobby);
        clearPendingSteamLobbyId(); //done with this invite, unlike a retry
        setLayout(getQueryParams(window.location.href)['lobby'] ? 'JoinBrowser' : 'Multiplayer');
    };

    const treeProps: TreeProps = { ...props, onGameTreeSelection, layout, setLayout, hover, retry, backOut };

    return (
        <div css={containerStyle}>
            <div css={menuStyle}>
                <LayoutPicker {...treeProps} />
            </div>
            <img
                id="ai_logo"
                css={logoStyle}
                src="assets/ui/logo.png"
            />
            {multiplayerLayouts.includes(layout) && <MultiplayerConnectionStatus />}
        </div>
    );
};

export const LayoutPicker = (props: TreeProps) => {
    switch (props.layout) {
        case 'Multiplayer':
            return <MultiplayerLayout {...props} />;
        case 'HostSteam':
            return <HostLayout {...props} transport='steam' />;
        case 'HostBrowser':
            return <HostLayout {...props} transport='browser' />;
        case 'JoinBrowser':
            return <JoinLayout {...props} />;
        case 'Spawn':
            return <SpawnLayout {...props} />;
        case 'Lobby':
        default:
            return <LobbyLayout {...props} />;
    }
};

const LobbyLayout = (props: TreeProps) => {
    const {
        hover,
        setLayout,
        mpAllowed,
        canContinue,
        startPending,
        onGameTreeSelection
    } = props;
    const [trainingLevel, setLevel] = useState(() => {
        let trainingLevel = 0;
        if (getTrainingLevel() === TrainingLevel.SecondGame) {
            trainingLevel = 1;
        } else if (getTrainingLevel() === TrainingLevel.MultiplayerIntro) {
            trainingLevel = 2;
        }
        return trainingLevel;
    });
    return (
        <>
            {canContinue && getTrainingLevel() === TrainingLevel.Trained &&
                <UIButton
                    css={buttonStyle}
                    onMouseEnter={hover}
                    onClick={() => onGameTreeSelection(GameTreeSelection.Continue)}
                    disabled={startPending}
                >
                    Continue
                </UIButton>
            }

            <UIButton
                css={buttonStyle}
                onMouseLeave={onButtonExit}
                onMouseEnter={() => {
                    hover();
                    setTooltipText('Play a solo game against the AI');
                }}
                onClick={() => onGameTreeSelection(GameTreeSelection.Solo)}
                disabled={startPending}
            >
                {
                    getTrainingLevel() === TrainingLevel.Trained ?
                        'Solo' :
                        `Tutorial ${trainingLevel ?? 0}/3`
                }
            </UIButton>
            <UIButton
                css={buttonStyle}
                onMouseLeave={onButtonExit}
                onMouseEnter={
                    () => {
                        hover();
                        setTooltipText('Play an asynchronous game against other players to improve your rank');
                    }
                }
                onClick={() => onGameTreeSelection(GameTreeSelection.Ranked)}
                disabled={!mpAllowed || startPending}
            >
                Ranked
            </UIButton>
            <ExperimentalStamp css={buttonStyle}>
                <UIButton
                    onMouseLeave={onButtonExit}
                    onMouseEnter={
                        () => {
                            hover();
                            setTooltipText('Play against other players');
                        }
                    }
                    onClick={() => {
                        clearConnectionStatus();
                        setLayout('Multiplayer');
                    }}
                    disabled={!mpAllowed || startPending}
                >
                    Multiplayer
                </UIButton>
            </ExperimentalStamp>
            <UIButton
                css={buttonStyle}
                onMouseLeave={onButtonExit}
                onClick={() => onGameTreeSelection(GameTreeSelection.Settings)}
            >
                Settings
            </UIButton>
            {
                process.env.is_demo &&
                <UIButtonSmall
                    onMouseEnter={hover}
                    onClick={openSteam}
                >
                    Wishlist!
                </UIButtonSmall>
            }
            {gameClient && gameClient.isElectron &&
                <UIButtonSmall
                    onMouseEnter={hover}
                    onClick={() => onGameTreeSelection(GameTreeSelection.Quit)}
                >
                    Quit
                </UIButtonSmall>
            }
            {
                getTrainingLevel() !== TrainingLevel.Trained &&
                <UIButtonSmall
                    onMouseEnter={hover}
                    onClick={() => {
                        onGameTreeSelection(GameTreeSelection.SkipTutorial);
                        setLevel(3);
                    }}
                    disabled={startPending}
                >
                    Skip Tutorial
                </UIButtonSmall>
            }
            <PrivacyPolicyLink />
        </>
    )
};

const HostLayout = (props: TransportProps) => {
    const {
        hover,
        setLayout,
        peerConnected,
        transport,
        retry,
        onGameTreeSelection
    } = props;
    const [lobbyCreated, setLobbyCreated] = useState(false);
    //The whole hosting session is one game (gameId is set throughout), so Start Game guards its own repeated clicks
    const [gameStarted, setGameStarted] = useState(false);
    const failed = useHasConnectionError();
    //Closing the Steam overlay tells us nothing, so once the lobby is up the button stays live and
    //re-opens the friends list. Otherwise backing out of the overlay leaves a lobby nobody was invited to.
    const canInviteAgain = transport === 'steam' && lobbyCreated && !failed && !peerConnected;

    return (
        <>
            <UIButton
                css={buttonStyle}
                onMouseLeave={onButtonExit}
                onMouseEnter={() => {
                    hover();
                    setTooltipText(canInviteAgain ?
                        'Open the Steam friends list again' :
                        transport === 'steam' ?
                            'Create a Steam lobby for your friends to join' :
                            'Create a lobby and get a link for other players to join'
                    );
                }}
                onClick={() => {
                    if (canInviteAgain) {
                        openSteamInviteDialog();
                        return;
                    }
                    setLobbyCreated(true);
                    onGameTreeSelection(transport === 'steam' ?
                        GameTreeSelection.HostViaSteam :
                        GameTreeSelection.HostViaBrowser
                    );
                }}
                disabled={lobbyCreated && !canInviteAgain}
            >
                {transport === 'steam' ?
                    (canInviteAgain ? 'Invite Again' : 'Invite Friend') :
                    'Create Lobby'
                }
            </UIButton>
            {/* Steam carries its own connection: the IP sharing warning only applies to the browser path */}
            {!lobbyCreated && transport === 'browser' && <P2PPrivacyWarning />}

            <UIButton
                css={buttonStyle}
                onMouseLeave={onButtonExit}
                onMouseEnter={() => {
                    hover();
                    setTooltipText('When all players are connected, start the game');
                }}
                onClick={() => {
                    setGameStarted(true);
                    onGameTreeSelection(GameTreeSelection.StartPeerToPeer);
                }}
                disabled={!peerConnected || gameStarted}
            >
                Start Game
            </UIButton>

            {failed &&
                <UIButton
                    css={buttonStyle}
                    onMouseLeave={onButtonExit}
                    onMouseEnter={() => {
                        hover();
                        setTooltipText('Start over and host again');
                    }}
                    onClick={retry}
                >
                    Try Again
                </UIButton>
            }

            <UIButtonSmall
                onMouseEnter={hover}
                onClick={() => {
                    onGameTreeSelection(GameTreeSelection.LeaveLobby);
                    setLobbyCreated(false);
                    setLayout('Multiplayer');
                }}
            >
                Back
            </UIButtonSmall>
        </>
    )
};

const JoinLayout = (props: TreeProps) => {
    const { hover, setLayout, onGameTreeSelection } = props;
    const [localLobbyId, setLocalLobbyId] = React.useState('');

    //Only ever reached by a tab opened from a join link: steam invites connect without asking
    useEffect(() => {
        setConnectionStep("Waiting to connect");
        setLocalLobbyId(getQueryParams(window.location.href)['lobby'] || '');
    }, []);

    const connect = () => {
        onGameTreeSelection(GameTreeSelection.JoinViaBrowser);
        setLayout('Spawn');
    }

    return (
        <>
            <UIButton
                css={buttonStyle}
                onMouseLeave={onButtonExit}
                onMouseEnter={() => {
                    hover();
                    setTooltipText('Join the game you were invited to');
                }}
                onClick={connect}
                disabled={localLobbyId === ''}
            >
                Connect to Game
            </UIButton>
            {/* No back button: this tab exists to join this game, there is nothing behind it */}
            <P2PPrivacyWarning />
        </>
    )
};

const MultiplayerLayout = (props: TreeProps) => {
    const { hover, setLayout, onGameTreeSelection } = props;
    // Steam invites need the desktop build with Steam running; hosting over WebRTC works
    // anywhere, and is how browser players get into a game.
    const [steamAvailable, setSteamAvailable] = useState(false);
    const [savedGame] = useState(readContinuableMultiplayerSave);

    useEffect(() => {
        let cancelled = false;
        isSteamNetworkingAvailable()
            .then(available => {
                if (!cancelled) {
                    setSteamAvailable(available);
                }
            })
            .catch(error => console.error("Failed to check Steam availability:", error));
        return () => { cancelled = true; };
    }, []);

    return (
        <>
            {savedGame &&
                <UIButton
                    css={buttonStyle}
                    onMouseLeave={onButtonExit}
                    onMouseEnter={() => {
                        hover();
                        setTooltipText('Invite a player back into your last game and carry on from where it stopped');
                    }}
                    onClick={() => {
                        clearConnectionStatus();
                        onGameTreeSelection(GameTreeSelection.ContinueMultiplayer);
                        //The invite has to be the same kind the game was hosted with
                        setLayout(savedGame.hostTransport === 'steam' ? 'HostSteam' : 'HostBrowser');
                    }}
                >
                    Continue Game
                </UIButton>
            }
            <UIButton
                css={buttonStyle}
                onMouseLeave={onButtonExit}
                onMouseEnter={() => {
                    hover();
                    setTooltipText('Send an invite to join your game to a Steam friend');
                }}
                onClick={() => {
                    onGameTreeSelection(GameTreeSelection.NewMultiplayerGame);
                    setLayout('HostSteam');
                }}
                disabled={!steamAvailable}
            >
                Invite via Steam
            </UIButton>
            <UIButton
                css={buttonStyle}
                onMouseLeave={onButtonExit}
                onMouseEnter={() => {
                    hover();
                    setTooltipText('Create a link another player can use to join from their browser without owning the game');
                }}
                onClick={() => {
                    onGameTreeSelection(GameTreeSelection.NewMultiplayerGame);
                    setLayout('HostBrowser');
                }}
            >
                Invite via Browser
            </UIButton>
            <UIButtonSmall
                onMouseEnter={hover}
                onClick={() => setLayout('Play')}
            >
                Back
            </UIButtonSmall>
        </>
    )
};

const SpawnLayout = (props: TreeProps) => {
    const { hover, retry, backOut } = props;
    const failed = useHasConnectionError();

    return (
        <>
            {!failed && <span>Waiting for host to start game</span>}
            {failed &&
                <UIButton
                    css={buttonStyle}
                    onMouseLeave={onButtonExit}
                    onMouseEnter={() => {
                        hover();
                        setTooltipText('Try connecting again');
                    }}
                    onClick={retry}
                >
                    Try Again
                </UIButton>
            }
            <UIButtonSmall
                onMouseEnter={hover}
                onClick={backOut}
            >
                Back
            </UIButtonSmall>
        </>
    )
};